import React, { useMemo, useState } from 'react';
import * as THREE from "three";
import { useThree } from '@react-three/fiber';
import { useSelector } from "react-redux";

import { const_var, params } from '../../../../../../redux/reducers/reducer';
import { controls } from '../../../../../../redux/reducers/common/eventReducer';

import { CSG } from "../../../../../../assets/libs/csg-lib";
import { extrudeSettings, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../../utils/Functions';
import useMouseEvents from '../../../../../../hooks/useMouseEvents';
import { MaterialInnerRoof, MaterialOuterRoof, MaterialRoofRidge } from '../../../../CustomMaterial';

const RoofModel = () => {
    const leftWallCloseState = useSelector((state) => state.reducer.leftWallCloseState)

    const { gl } = useThree();

    const texture_metalSprite = const_var.metalTexture;

    const [overHangWidth, setOverHangWidth] = useState(0);
    const [overHangLength, setOverHangLength] = useState(0);
    const [isEqualHeight, setIsEqualHeight] = useState(false);
    const [isEqualFrontLeanHeight, setIsEqualFrontLeanHeight] = useState(false);
    const [isEqualBackLeanHeight, setIsEqualBackLeanHeight] = useState(false);
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(params.lean_p_d);
    const [patternPosition, setPatternPosition] = useState({x:0,y:0,z:0});
    const [curveRoofPositionZ, setCurveRoofPositionZ] = useState(0)
    const [curveRoofPatternDepth, setCurveRoofPatternDepth] = useState(0)
    // const [isRoofPanel, setIsRoofPanel] = useState(false);

    const roofThickness = 0.005;
    const trimThickness = 0.2;
    const ridgeThickness = 0.5
    const cbFrontBackWallOffset = 0.16;
    const wallThickness = 0.005
    const offset = 0.16

    const roofPitchHeight = (params.b_l_t_r_p / 12) * params.lean_p_w;
    const trimOffsetHeight = (params.p_r_p / 12) * trimThickness;
    const roofAngle = Math.atan((roofPitchHeight - (isEqualHeight && trimOffsetHeight)) / (params.lean_p_w - (isEqualHeight && trimThickness)));
    const roofRidgeOffsetHeight = 0.03 / Math.cos(roofAngle);
    const roofCutShapeWidth = Math.sqrt(Math.pow(params.lean_p_w, 2) * 2)
    const leanRoofHeight =(params.b_l_t_r_p / 12) * params.lean_p_w;
    const leanRoofLeftHeight = (params.b_l_t_r_pF / 12) * params.leanF_p_w;
    const leanRoofRightHeight = (params.b_l_t_r_pB / 12) * params.leanB_p_w;
    const leanRoofWidth = Math.sqrt(Math.pow((params.lean_p_w - ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), 2) + Math.pow((roofPitchHeight - ((isEqualHeight && params.p_r_s !== 1) ?  trimOffsetHeight : 0 )), 2));
    const overHangHorizon = Math.cos(roofAngle) * (overHangWidth);
    const diagonalLength = overHangWidth + leanRoofWidth;
    
    const mainColor = useMemo(() => (params.p_r_c.replace("0x","#")), [params.p_r_c]) 
    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 ) ? true : false
    
    // const texture = const_var.metalTexture;

    // useMemo(() => {
    //     textureAnisotropy(gl, texture, 1.3, 1.3, params.p_v_w ? Math.PI / 2 : Math.PI);
    // }, [params.p_v_w, gl, texture])

    // const wallColor = useMemo(() => {

    //     if ((controls.isCheckActiveTab === 'doorwindow' && const_var.focusedSection !== 'leftlean')  || (params.is_translusant )) {
    //         return '#ffffff'
    //     } else {
    //         return params.p_w_c.replace('0x', '#');
    //     }
    // }, [params.p_w_c, controls.isCheckActiveTab, const_var.focusedSection, params.is_translusant])

    const offSetValue = useMemo(() => {
        let offSetValue;
        if (params.p_r_s === 1) {
            const height =  Math.sin(roofAngle) * offset;
            offSetValue = (height == 0 || Math.tan(roofAngle) == 0) ? 0 : height / Math.tan(roofAngle)
        } else {
            offSetValue = 0
        }
        
        return offSetValue
    }, [roofAngle , params.p_r_s])

    const verticalMap = (texture_metalSprite) ? texture_metalSprite.clone() : texture_metalSprite;
    const horizontalMap = (texture_metalSprite) ? texture_metalSprite.clone() : texture_metalSprite;
    if(horizontalMap && verticalMap){
        textureAnisotropy(gl, verticalMap, 1.3, 1.3, Math.PI);
        verticalMap.needsUpdate = true;
        textureAnisotropy(gl, horizontalMap, 1.3, 1.3, Math.PI / 2);
        horizontalMap.offset.setY(params.o_v_h_n_g_e ? 0.5 : 0.15)
        horizontalMap.needsUpdate = true;
    }
    
    
    const roofTexture = useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) {
            return verticalMap;
        } else if (params.p_r_s === 3) {
            return horizontalMap;
        }
    }, [horizontalMap, params.p_r_s, verticalMap]);

    const lengthLeftOffset = useMemo(() => (
        (isEqualFrontLeanHeight && !isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d || Number(params.leantoAlignmentLeft) === 2)) || 
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.lean_p_d && Number(params.leantoAlignmentLeft) === 2)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d))
    ) ? 0.23 : overHangLength, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.lean_p_d, params.leantoAlignmentLeft, overHangLength ]);
    
    const lengthRightOffset = useMemo(() => (
        (!isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d || Number(params.leantoAlignmentLeft) === 3)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.lean_p_d && Number(params.leantoAlignmentLeft) === 3)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d))
    ) ? 0.23 : overHangLength, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.lean_p_d, params.leantoAlignmentLeft, overHangLength]);
    
    const patternLengthOffset = useMemo(() => {
        let offset = 0;
        if((isEqualFrontLeanHeight && !isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d || Number(params.leantoAlignmentLeft) === 2)) ||
            (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.lean_p_d && Number(params.leantoAlignmentLeft) === 2)) ||
            (!isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d || Number(params.leantoAlignmentLeft) === 3)) ||
            (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.lean_p_d && Number(params.leantoAlignmentLeft) === 3))){
                offset =  -(overHangLength - 0.23);
        } else if(isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d)){
                offset = -(overHangLength - 0.23) * 2;
        } else offset = 0;
        return offset;

    }, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.lean_p_d, params.leantoAlignmentLeft, overHangLength]);

    const patternPositionOffset = useMemo(() => {
        let offset = 0;
        if((isEqualFrontLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d || Number(params.leantoAlignmentLeft) === 2)) 
            || (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.lean_p_d && Number(params.leantoAlignmentLeft) === 2))){
                offset = -(overHangLength - 0.23)
        } else offset = 0;

        return offset;
    }, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.lean_p_d, params.leantoAlignmentLeft, overHangLength])

    const regularPatternPositionOffset = useMemo(() => {
        let offset = 0;
        if(isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.lean_p_d || Number(params.leantoAlignmentLeft) === 3)){
                offset = -(overHangLength - 0.23)
        } else offset = 0;

        return offset;
    }, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.lean_p_d, params.leantoAlignmentLeft, overHangLength])

    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side']!=undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end']!=undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }
  
    const roofPanModel = useMemo(() => {
        let offset = 0;
        if (params.p_r_s === 1) offset = 0.84;
        else offset = 0;
        
        const model = new THREE.Shape();

        if( params.add_left_front_lean_porch !== true && params.add_left_back_lean_porch !== true ) {
            model.moveTo(-(params.lean_p_d / 2 + lengthRightOffset), 0 + overHangWidth - offset);
            model.lineTo((params.lean_p_d / 2 + lengthLeftOffset), 0 + overHangWidth - offset);
            model.lineTo((params.lean_p_d / 2 + lengthLeftOffset), -leanRoofWidth);
            model.lineTo(-(params.lean_p_d / 2 + lengthRightOffset), -leanRoofWidth);

        } else if( params.add_left_front_lean_porch === true && params.add_left_back_lean_porch !== true ) {
            model.moveTo((params.lean_p_d / 2 + params.lean_p_w + cbFrontBackWallOffset + overHangHorizon - offSetValue), 0 + overHangWidth - offset);
            model.lineTo(-(params.lean_p_d / 2 + lengthRightOffset), 0 + overHangWidth - offset);
            model.lineTo(-(params.lean_p_d / 2 + lengthRightOffset), -leanRoofWidth);
            model.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRoofWidth);
            model.closePath();

        } else if( params.add_left_front_lean_porch !== true && params.add_left_back_lean_porch === true ) {
            model.moveTo(-(params.lean_p_d / 2 + cbFrontBackWallOffset + overHangHorizon + params.lean_p_w - offSetValue) , 0 + overHangWidth - offset);
            model.lineTo((params.lean_p_d / 2 + lengthLeftOffset), 0 + overHangWidth - offset);
            model.lineTo((params.lean_p_d / 2 + lengthLeftOffset), -leanRoofWidth);
            model.lineTo(-(params.lean_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRoofWidth);
            model.closePath();

        } else {
            // model.moveTo(-((params.leanR_p_d / 2 + cbFrontBackWallOffset + overHangHorizon) + params.leanR_p_w - offSetValue), 0 + overHangWidth - offset);
            model.moveTo(-(params.lean_p_d / 2 + cbFrontBackWallOffset + overHangHorizon + params.lean_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset + overHangHorizon + params.lean_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRoofWidth);
            model.lineTo(-(params.lean_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRoofWidth);
            model.closePath();
        }
        
        return model;
    }, [params.p_r_s, params.add_left_front_lean_porch, params.add_left_back_lean_porch, params.lean_p_d, overHangWidth, overHangHorizon, leanRoofWidth, params.lean_p_h, params.lean_p_w, params.b_l_t_r_p, params.o_v_h_n_g_e, params.o_v_h_n_g, offSetValue, roofAngle, isEqualHeight, lengthRightOffset, lengthLeftOffset, params.p_d, params.leantoAlignmentLeft]);

    const roofTopTrimRidgeModelObj = useMemo(() => {
        let rightTrimRidge;
        let leftTrimRidge;

        const roofTopLeftTrimRidgeModel = new THREE.Shape();
            roofTopLeftTrimRidgeModel.moveTo(-((params.lean_p_d / 2) + params.lean_p_w + overHangHorizon), 0 + overHangWidth);
            roofTopLeftTrimRidgeModel.lineTo(-((params.lean_p_d / 2) + params.lean_p_w + overHangHorizon) + ridgeThickness, 0 + overHangWidth);
            roofTopLeftTrimRidgeModel.lineTo(-(params.lean_p_d / 2 + (isEqualHeight ? trimThickness : 0)) + ridgeThickness, -leanRoofWidth);
            roofTopLeftTrimRidgeModel.lineTo(-(params.lean_p_d / 2 + (isEqualHeight ? trimThickness : 0)), -leanRoofWidth);
            roofTopLeftTrimRidgeModel.closePath();

        const roofTopRightTrimRidgeModel = new THREE.Shape();
            roofTopRightTrimRidgeModel.moveTo((params.lean_p_d / 2) + params.lean_p_w + overHangHorizon - ridgeThickness, 0 + overHangWidth);
            roofTopRightTrimRidgeModel.lineTo((params.lean_p_d / 2) + params.lean_p_w + overHangHorizon, 0 + overHangWidth);
            roofTopRightTrimRidgeModel.lineTo((params.lean_p_d / 2 + (isEqualHeight ? trimThickness : 0)), -leanRoofWidth);
            roofTopRightTrimRidgeModel.lineTo((params.lean_p_d / 2 - ridgeThickness + (isEqualHeight ? trimThickness : 0)), -leanRoofWidth);
            roofTopRightTrimRidgeModel.closePath();

        const roofHeight = params.p_r_p / 12 * (params.p_w / 2 + 0.75) 

        let lengthOffset;
        let delModelPosOffsetX;
        
        if(params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = -1.05;
            } else if(sideOverHangSize === 2 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = -2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = -2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 1.05){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = -1.05;
            } else {
                lengthOffset = 2;
                delModelPosOffsetX = -1.25;
            }
        } else if(!params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05){
                delModelPosOffsetX = -1.05;
            } else if(sideOverHangSize === 2){
                delModelPosOffsetX = -2;
            } else {
                delModelPosOffsetX = -1.25;
            }
            lengthOffset = 1.5;
        } else if(params.o_v_h_n_g_e && !params.o_v_h_n_g) {
            if(endOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
            } else {
                lengthOffset = 2;
            }
            delModelPosOffsetX = -0.75;
        } else {
            lengthOffset = 1.5
            delModelPosOffsetX = -0.75;
        }

        const roofDelModel = new THREE.Shape();
        roofDelModel.moveTo(-params.p_w / 2 + delModelPosOffsetX, params.p_h);
        roofDelModel.lineTo(0, params.p_h + roofHeight);
        roofDelModel.lineTo(0, 0);
        roofDelModel.lineTo(-params.p_w / 2 + delModelPosOffsetX, 0);
        roofDelModel.closePath();

        const meshRoofTopRightTrimRidgeModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofTopRightTrimRidgeModel, extrudeSettings(0.03)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
        const meshRoofTopLeftTrimRidgeModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofTopLeftTrimRidgeModel, extrudeSettings(0.03)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
        const roofDelModelObj = new THREE.Mesh(new THREE.ExtrudeGeometry(roofDelModel, extrudeSettings(params.p_d + lengthOffset)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
         
        const meshRoofTopRightTrimRidgeModel_Temp = meshRoofTopRightTrimRidgeModel.clone();
        meshRoofTopRightTrimRidgeModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_w) , params.lean_p_h + roofRidgeOffsetHeight + 0.068, 0.16);
        meshRoofTopRightTrimRidgeModel_Temp.rotateZ(Math.PI / 2 + roofAngle);
        meshRoofTopRightTrimRidgeModel_Temp.updateMatrixWorld();

        const meshRoofTopRightTrimRidgeModel_Temp1 = meshRoofTopRightTrimRidgeModel_Temp.clone();
        meshRoofTopRightTrimRidgeModel_Temp1.rotateY(-Math.PI / 2);
        meshRoofTopRightTrimRidgeModel_Temp1.updateMatrixWorld();

        const meshRoofTopLeftTrimRidgeModel_Temp = meshRoofTopLeftTrimRidgeModel.clone();
        meshRoofTopLeftTrimRidgeModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h + roofRidgeOffsetHeight + 0.068,  - 0.16);
        meshRoofTopLeftTrimRidgeModel_Temp.rotateZ(Math.PI / 2 + roofAngle);
        meshRoofTopLeftTrimRidgeModel_Temp.updateMatrixWorld();

        const meshRoofTopLeftTrimRidgeModel_Temp1 = meshRoofTopLeftTrimRidgeModel_Temp.clone();
        meshRoofTopLeftTrimRidgeModel_Temp1.rotateY(-Math.PI / 2);
        meshRoofTopLeftTrimRidgeModel_Temp1.updateMatrixWorld();


        const roofDelModelObj_Temp = roofDelModelObj.clone();
        roofDelModelObj_Temp.position.set(0, 0, -params.p_d / 2 - lengthOffset / 2 - params.lean_p_pos);
        roofDelModelObj_Temp.updateMatrixWorld();

        const meshRoofTopRightTrimRidgeModel_CSG = CSG.fromMesh(meshRoofTopRightTrimRidgeModel_Temp1);
        const meshRoofTopLeftTrimRidgeModel_CSG = CSG.fromMesh(meshRoofTopLeftTrimRidgeModel_Temp1);

        const roofDelModelObj_CSG = CSG.fromMesh(roofDelModelObj_Temp)

        let meshRoofTopRightTrimRidgeModelCSG;
        let meshRoofTopLeftTrimRidgeModelCSG;
        if(isEqualHeight){
            meshRoofTopRightTrimRidgeModelCSG = meshRoofTopRightTrimRidgeModel_CSG.subtract(roofDelModelObj_CSG);
            meshRoofTopLeftTrimRidgeModelCSG = meshRoofTopLeftTrimRidgeModel_CSG.subtract(roofDelModelObj_CSG);
        } else{
            meshRoofTopRightTrimRidgeModelCSG = meshRoofTopRightTrimRidgeModel_CSG
            meshRoofTopLeftTrimRidgeModelCSG = meshRoofTopLeftTrimRidgeModel_CSG
        }
        
        const CSGRoofTopRightTrimRidgeModel = CSG.toMesh(meshRoofTopRightTrimRidgeModelCSG, new THREE.Matrix4());
        const CSGRoofTopLeftTrimRidgeModel = CSG.toMesh(meshRoofTopLeftTrimRidgeModelCSG, new THREE.Matrix4());

        rightTrimRidge = CSGRoofTopRightTrimRidgeModel.geometry;
        leftTrimRidge = CSGRoofTopLeftTrimRidgeModel.geometry;

        return {rightTrimRidge, leftTrimRidge}

    }, [params.lean_p_d, params.lean_p_w, overHangWidth, overHangHorizon, params.b_l_t_r_p, isEqualHeight, leanRoofWidth, params.p_r_p, params.p_w, params.p_h, params.p_d, params.o_v_h_n_g, params.o_v_h_n_g_e, params.lean_p_h, roofRidgeOffsetHeight, sideOverHangSize, endOverHangSize, params.p_r_s, params.lean_p_pos])

    const roofModelObj = useMemo(() => {
        let mainOuter, curveOuter, curveInner;

        const roofCutShape = new THREE.Shape();
        roofCutShape.moveTo(-roofCutShapeWidth - 2.5, 0);
        roofCutShape.lineTo(-roofCutShapeWidth - 2.5, params.lean_p_h + (params.b_l_t_r_p / 12*((params.lean_p_w))) + 2);
        roofCutShape.lineTo(roofCutShapeWidth + 2.5, params.lean_p_h + (params.b_l_t_r_p / 12*((params.lean_p_w))) + 2);
        roofCutShape.lineTo(roofCutShapeWidth + 2.5, 0);
        roofCutShape.closePath()

        const roofEndCutShape = new THREE.Shape();
        roofEndCutShape.moveTo(-2, 0.2);
        roofEndCutShape.lineTo(0, 0.2);
        roofEndCutShape.lineTo(0, -0.2);
        roofEndCutShape.lineTo(-2, -0.2);
        roofEndCutShape.closePath()

        const curveRoofCutShape = new THREE.Shape();
        curveRoofCutShape.moveTo(-params.lean_p_w * 2, 0)
        curveRoofCutShape.lineTo(-params.lean_p_w * 2, params.lean_p_h * 3);
        curveRoofCutShape.lineTo(params.lean_p_w * 2, params.lean_p_h * 3);
        curveRoofCutShape.lineTo(params.lean_p_w * 2, 0)
    
        const curveInnerModel = new THREE.Shape();        
        curveInnerModel.moveTo(-0.1, 0.04);
        curveInnerModel.lineTo(-0.1, 0.04 + const_var.roofRegularDst);
        curveInnerModel.quadraticCurveTo(-0.1, const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10, 
                                        -0.15 + const_var.roofRegularDst, const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12) - 0.02);
        curveInnerModel.lineTo(params.lean_p_w - 0.15, (params.lean_p_w - 0.1) * params.b_l_t_r_p / 12 + const_var.roofRegularDst * 2);
        curveInnerModel.lineTo(params.lean_p_w - 0.15, (params.lean_p_w - 0.1) * params.b_l_t_r_p / 12 + const_var.roofRegularDst * 2 + 0.01);
        curveInnerModel.lineTo(-0.15 + const_var.roofRegularDst, const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12) - 0.01);
        curveInnerModel.quadraticCurveTo(-0.1, const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10, 
                                        -0.11, 0.04 + const_var.roofRegularDst);
        curveInnerModel.moveTo(-0.11, 0.04);
        curveInnerModel.closePath();

        const roofHeight = params.p_r_p / 12 * (params.p_w / 2 + 0.75)

        let lengthOffset;
        let delModelPosOffsetX;

           
        if(params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = -1.05;
            } else if(sideOverHangSize === 2 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = -2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = -2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 1.05){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = -1.05;
            } else {
                lengthOffset = 2;
                delModelPosOffsetX = -1.25;
            }
        } else if(!params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05){
                delModelPosOffsetX = -1.05;
            } else if(sideOverHangSize === 2){
                delModelPosOffsetX = -2;
            } else {
                delModelPosOffsetX = -1.25;
            }
            lengthOffset = 1.5;
        } else if(params.o_v_h_n_g_e && !params.o_v_h_n_g) {
            if(endOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
            } else {
                lengthOffset = 2;
            }
            delModelPosOffsetX = -0.75;
        } else {
            lengthOffset = 1.5
            delModelPosOffsetX = -0.75;
        }
  
        const roofDelModel = new THREE.Shape();
        roofDelModel.moveTo(-params.p_w / 2 + delModelPosOffsetX, params.p_h);
        roofDelModel.lineTo(0, params.p_h + roofHeight);
        roofDelModel.lineTo(0, 0);
        roofDelModel.lineTo(-params.p_w / 2 + delModelPosOffsetX, 0);
        roofDelModel.closePath();

        const outerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern,"roof"), extrudeSettings(patternDepth + 0.005)));
        const curveOuterMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPatternCurve(2, params.b_l_t_r_p.toString(), isPattern), extrudeSettings(curveRoofPatternDepth)));
        const curveInnerMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(curveInnerModel, extrudeSettings(curveRoofPatternDepth)));

        const mainRoofCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape, extrudeSettings(roofCutShapeWidth)));
        const curveRoofCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(curveRoofCutShape, extrudeSettings(params.lean_p_w * 2)));
        const mainRoofEndCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofEndCutShape,  params.p_r_s != 3 ? extrudeSettings(patternDepth) : extrudeSettings(patternLength) ))

        const roofDelModelObj = new THREE.Mesh(new THREE.ExtrudeGeometry(roofDelModel, extrudeSettings(params.p_d + lengthOffset)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));

        let leftLeanRoofCutMesh = mainRoofEndCutMesh.clone();
        leftLeanRoofCutMesh.position.set(-(params.p_w / 2 + trimThickness), params.p_h - trimOffsetHeight, - (params.p_r_s != 3 ? patternDepth / 2 : patternLength / 2))
        leftLeanRoofCutMesh.rotation.set(0, 0, -(Math.PI - roofAngle))
        leftLeanRoofCutMesh.updateMatrixWorld();

        let curveRoofCutMesh1 = curveRoofCutMesh.clone();
        curveRoofCutMesh1.rotation.set(0, Math.PI / 4, 0);
        curveRoofCutMesh1.position.set(-(params.p_w / 2 + params.lean_p_w + overHangWidth), 0, params.p_d / 2 + params.lean_p_w + overHangWidth + cbFrontBackWallOffset - params.lean_p_pos);
        curveRoofCutMesh1.updateMatrixWorld();

        let curveRoofCutMesh2 = curveRoofCutMesh.clone();
        curveRoofCutMesh2.rotation.set(0, Math.PI / 4 * 3, 0);
        curveRoofCutMesh2.position.set(-(params.p_w / 2 + params.lean_p_w + overHangWidth), 0, -(params.p_d / 2 + params.lean_p_w + overHangWidth + cbFrontBackWallOffset) - params.lean_p_pos);
        curveRoofCutMesh2.updateMatrixWorld();

        let roofCutMesh1 = mainRoofCutMesh.clone();
        roofCutMesh1.rotation.set(0, Math.PI / 4, 0);
        roofCutMesh1.position.set(-(params.p_w / 2 + params.lean_p_w), 0, params.p_d / 2 + params.lean_p_w + cbFrontBackWallOffset - params.lean_p_pos);
        roofCutMesh1.visible = false;
        roofCutMesh1.updateMatrixWorld();
        
        let roofCutMesh2 = mainRoofCutMesh.clone();
        roofCutMesh2.rotation.set(0, Math.PI / 4 * 3, 0);
        roofCutMesh2.position.set(-(params.p_w / 2 + params.lean_p_w), 0, -(params.p_d / 2 + params.lean_p_w + cbFrontBackWallOffset) - params.lean_p_pos);
        roofCutMesh2.visible = false;
        roofCutMesh2.updateMatrixWorld();

        let outerRoofMeshPosUpdate = outerRoofMesh.clone();
        outerRoofMeshPosUpdate.position.set(patternPosition.x,patternPosition.y,patternPosition.z);
        outerRoofMeshPosUpdate.rotation.set(Math.PI / 2,(params.p_r_s === 3) ? 0 : Math.PI / -2,Math.PI)
        outerRoofMeshPosUpdate.updateMatrixWorld();

        // Create a temporary object to apply the parent group's transformations
        let tempGroup1 = new THREE.Object3D();
        tempGroup1.rotation.set(0, -Math.PI / 2, 0);
        tempGroup1.updateMatrixWorld();
        
        let tempGroup2 = new THREE.Object3D();
        tempGroup2.position.set(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h + 0.025 + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 + 0.14 * (params.b_l_t_r_p / 12) - 0.015: 0), 0)
        tempGroup2.rotation.set(0, 0, Math.PI / 2 + roofAngle);
        tempGroup2.updateMatrixWorld();

        // Apply the parent's world matrix to the outerRoofMeshPosUpdate
        outerRoofMeshPosUpdate.applyMatrix4(tempGroup1.matrixWorld);
        outerRoofMeshPosUpdate.updateMatrixWorld();

        outerRoofMeshPosUpdate.applyMatrix4(tempGroup2.matrixWorld)
        outerRoofMeshPosUpdate.updateMatrixWorld();

        // Now outerRoofMeshPosUpdate has the combined transformations

        let cutMesh = mainRoofCutMesh.clone();
        cutMesh.position.set(0,0, -2);
        cutMesh.updateMatrixWorld();

        let curveOuterMeshPosUpdate = curveOuterMesh.clone();
        curveOuterMeshPosUpdate.position.set(-(params.p_w / 2 + params.lean_p_w - 0.12) - 0.14, params.lean_p_h + 0.1*(params.b_l_t_r_p/12) + 0.035, curveRoofPositionZ)
        curveOuterMeshPosUpdate.rotation.set(0, 0, 0)
        curveOuterMeshPosUpdate.updateMatrixWorld()
        
        let curveInnerMeshPosUpdate = curveInnerMesh.clone();
        curveInnerMeshPosUpdate.position.set(-(params.p_w / 2 + params.lean_p_w - 0.12), params.lean_p_h + 0.1*(params.b_l_t_r_p/12), curveRoofPositionZ)
        curveInnerMeshPosUpdate.rotation.set(0, 0, 0)
        curveInnerMeshPosUpdate.updateMatrixWorld()

        const roofDelModelObj_Temp = roofDelModelObj.clone();
        roofDelModelObj_Temp.position.set(0, 0, -params.p_d / 2 - lengthOffset / 2 - params.lean_p_pos);
        roofDelModelObj_Temp.updateMatrixWorld();

        const outerRoofMesh_CSG = CSG.fromMesh(outerRoofMeshPosUpdate);
        const curveOuterMesh_CSG = CSG.fromMesh(curveOuterMeshPosUpdate)
        const curveInnerMesh_CSG = CSG.fromMesh(curveInnerMeshPosUpdate)
        
        const cutMesh_CSG1 = CSG.fromMesh(roofCutMesh1);
        const cutMesh_CSG2 = CSG.fromMesh(roofCutMesh2);
        const curveRoofCut_CSG1 = CSG.fromMesh(curveRoofCutMesh1)
        const curveRoofCut_CSG2 = CSG.fromMesh(curveRoofCutMesh2)
            
        let roofMeshFinalCsg_temp
        let roofMeshFinalCsgTemp

        if(params.add_left_front_lean_porch && params.add_left_back_lean_porch) {

            roofMeshFinalCsg_temp = outerRoofMesh_CSG.subtract(cutMesh_CSG1);
            roofMeshFinalCsgTemp = roofMeshFinalCsg_temp.subtract(cutMesh_CSG2)
        } else if (params.add_left_front_lean_porch && !params.add_left_back_lean_porch) {

            roofMeshFinalCsgTemp = outerRoofMesh_CSG.subtract(cutMesh_CSG1);
        } else if (!params.add_left_front_lean_porch && params.add_left_back_lean_porch){

            roofMeshFinalCsgTemp = outerRoofMesh_CSG.subtract(cutMesh_CSG2);
        } else {
            roofMeshFinalCsgTemp = outerRoofMesh_CSG
        }

        const curveOuterMesh_CSG_temp_1 = curveOuterMesh_CSG.subtract(curveRoofCut_CSG1);
        const curveOuterMesh_CSG_temp_2 = curveOuterMesh_CSG_temp_1.subtract(curveRoofCut_CSG2);
        const curveInnerMesh_CSG_temp_1 = params.add_left_front_lean_porch ? curveInnerMesh_CSG.subtract(curveRoofCut_CSG1) : curveInnerMesh_CSG;
        const curveInnerMesh_CSG_temp_2 = params.add_left_back_lean_porch ? curveInnerMesh_CSG_temp_1.subtract(curveRoofCut_CSG2) : curveInnerMesh_CSG_temp_1;

        const roofMeshFinal = CSG.toMesh(roofMeshFinalCsgTemp,new THREE.Matrix4())
        const curveOuterMeshFinal = CSG.toMesh(curveOuterMesh_CSG_temp_2, new THREE.Matrix4())
        const curveInnerMeshFinal = CSG.toMesh(curveInnerMesh_CSG_temp_2, new THREE.Matrix4())

        mainOuter = roofMeshFinal.geometry
        curveOuter = curveOuterMeshFinal.geometry
        curveInner = curveInnerMeshFinal.geometry

        return {mainOuter, curveOuter, curveInner}
    }, [params.leanR_p_h, params.lean_p_h, params.lean_p_w, params.lean_p_d, params.b_l_t_r_p, roofCutShapeWidth, patternLength, patternDepth, params.p_w, params.p_h, params.p_d, params.lean_p_pos, overHangWidth, patternPosition, params.p_r_s, roofAngle, curveRoofPositionZ, params.o_v_h_n_g, params.o_v_h_n_g_e, params.p_r_p, curveRoofPatternDepth, params.add_left_front_lean_porch, params.add_left_back_lean_porch, isEqualHeight, sideOverHangSize, endOverHangSize, controls.isCheckActiveTab, const_var.focusedSection, isPattern])

    // const roofPanel = useMemo(() => {

    //     let frontAlignmentVal = (params.leantoAlignmentLeft || params.leantoAlignmentLeft == 1) ? overHangLength : 0
    //     let backAlignmentVal = (params.leantoAlignmentLeft == 3 || params.leantoAlignmentLeft == 1) ? overHangLength : 0

    //     const model = new THREE.Shape();
    //     model.moveTo(-(params.lean_p_d / 2 ) - frontAlignmentVal, params.p_h - (params.lean_p_h + (params.lean_p_w*(params.b_l_t_r_p/12))));
    //     model.lineTo(params.lean_p_d / 2 + backAlignmentVal, params.p_h - (params.lean_p_h + (params.lean_p_w*(params.b_l_t_r_p/12))));
    //     model.lineTo(params.lean_p_d / 2 + backAlignmentVal, 0);
    //     model.lineTo(-params.lean_p_d / 2 - frontAlignmentVal, 0);
    //     model.closePath();

    //     return model;
    // }, [params.lean_p_d, params.lean_p_h, params.p_d, params.p_h, params.lean_p_w,params.b_l_t_r_p, params.leantoAlignmentLeft, overHangLength])

    // useMemo(() => {
    //     let panelHeight = (params.lean_p_h + (params.lean_p_w*(params.b_l_t_r_p/12))) - 0.2
    //     if(params.p_d != params.lean_p_d && leftWallCloseState == panelHeight){
    //         setIsRoofPanel(false)
    //     }else{
    //         setIsRoofPanel(false)
    //     }
    // }, [params.lean_p_w, params.lean_p_h, params.lean_p_d, params.p_d, params.p_h, params.b_l_t_r_p,leftWallCloseState])

    useMemo(() => {
        if(params.p_h === leanRoofHeight + params.lean_p_h) {
            setIsEqualHeight(true)
        } else {
            setIsEqualHeight(false)
        }
        if(params.add_front_lean && (leanRoofHeight + params.lean_p_h === leanRoofLeftHeight + params.leanF_p_h) && ((params.p_w === params.leanF_p_d) || Number(params.leantoAlignmentFront) === 2))
         setIsEqualFrontLeanHeight(true);
        else setIsEqualFrontLeanHeight(false)
        if(params.add_back_lean && (leanRoofHeight + params.lean_p_h === leanRoofRightHeight + params.leanB_p_h) && ((params.p_w === params.leanB_p_d) || Number(params.leantoAlignmentBack) === 3))
         setIsEqualBackLeanHeight(true);
        else setIsEqualBackLeanHeight(false)
    }, [params.p_h, params.lean_p_h, leanRoofHeight, leanRoofLeftHeight, leanRoofRightHeight, params.add_front_lean, params.add_back_lean, params.leanB_p_d, params.leanF_p_d, params.p_w, params.leantoAlignmentFront, params.leantoAlignmentBack, params.leanF_p_h, params.leanB_p_h])

    useMemo(() => {

        if (params.p_r_s === 2 || params.p_r_s === 1) {
            if(params.p_r_s === 2) {
                if (params.o_v_h_n_g === true){
                    setOverHangWidth(sideOverHangSize)
                } else {
                    setOverHangWidth(0.4);
                }
                if (params.o_v_h_n_g_e === true) {
                    //setOverHangLength(endOverHangSize)
                    setOverHangLength(endOverHangSize == 0.5 ? 0.75 : endOverHangSize )
                } else {
                    setOverHangLength(0.75)
                }
            } else {
                setOverHangWidth(0)
                setOverHangLength(0.75)
            }
            if(params.p_r_s === 2) setPatternLength(diagonalLength ); else setPatternLength(diagonalLength + 0.02 - const_var.roofRegularDst / Math.cos(Math.atan(params.b_l_t_r_p / 12)));
            // setPatternDepth(params.lean_p_d + ((overHangLength)*2))
            // setPatternPosition({x:(params.lean_p_d + ((overHangLength)*2) )/2,y:(overHangWidth)+(roofThickness * 2) + (params.p_r_s === 1 && -0.1),z:0})
            if( params.add_left_front_lean_porch === true && params.add_left_back_lean_porch !== true ) {
                setPatternDepth((params.lean_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.lean_p_w + 0.5 + patternLengthOffset)
                setPatternPosition({x:(params.lean_p_d/2) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.lean_p_w + 0.5 + patternPositionOffset,y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)),z:0.005})
                setCurveRoofPatternDepth((params.lean_p_d) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanF_p_w + patternLengthOffset + 1)
                setCurveRoofPositionZ( -params.lean_p_d / 2 - lengthRightOffset)
            } else if( params.add_left_front_lean_porch !== true && params.add_left_back_lean_porch === true ) {
                setPatternDepth((params.lean_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.lean_p_w + 0.15 + patternLengthOffset / 2)
                setPatternPosition({x:(params.lean_p_d/2) + overHangLength + patternPositionOffset,y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)),z:0.005})
                setCurveRoofPatternDepth((params.lean_p_d) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanB_p_w + 1 + patternLengthOffset)
                setCurveRoofPositionZ( -params.lean_p_d / 2 - params.leanB_p_w - 0.83)
            } else if( params.add_left_front_lean_porch === true && params.add_left_back_lean_porch === true ) {
                setPatternDepth((params.lean_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.lean_p_w*2) + 0.27)
                setPatternPosition({x:(params.lean_p_d/2) + overHangWidth + cbFrontBackWallOffset + params.lean_p_w + 0.14,y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)),z:0.005})
                setCurveRoofPatternDepth((params.lean_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + params.leanB_p_w + params.leanF_p_w + 1)
                setCurveRoofPositionZ(-(params.p_d / 2 + params.lean_p_w) - 0.7)
            } else {
                setPatternDepth(params.lean_p_d + ((overHangLength)*2) + patternLengthOffset)
                setPatternPosition({x:(params.lean_p_d + ((overHangLength)*2))/2 + patternPositionOffset,y:-(diagonalLength-(overHangWidth)-(roofThickness * 2) + (params.p_r_s === 1 && -0.1)) + (params.p_r_s === 1 && -0.1),z:0})
                setCurveRoofPatternDepth(params.lean_p_d + ((overHangLength)*2) + patternLengthOffset - 0.38)
                setCurveRoofPositionZ( -(params.lean_p_d / 2) - regularPatternPositionOffset - 0.56)
            }
        } else if (params.p_r_s === 3) {
            if (params.o_v_h_n_g === true){
                setOverHangWidth(sideOverHangSize)
            } else {
                setOverHangWidth(0.4);
            }
            if (params.o_v_h_n_g_e === true) {
                //setOverHangLength(endOverHangSize)
                setOverHangLength(endOverHangSize == 0.5 ? 0.75 : endOverHangSize )
            } else {
                setOverHangLength(0.75)
            }
            if( params.add_left_front_lean_porch === true && params.add_left_back_lean_porch !== true ) {
                setPatternLength((params.lean_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.lean_p_w + patternLengthOffset)
                setPatternPosition({x:(params.lean_p_d/2) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.lean_p_w + patternPositionOffset,y:(overHangWidth)+(roofThickness * 2),z:0.005})
            } else if( params.add_left_front_lean_porch !== true && params.add_left_back_lean_porch === true ) {
                setPatternLength((params.lean_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.lean_p_w + patternLengthOffset / 2)
                setPatternPosition({x:(params.lean_p_d/2) + overHangLength + patternPositionOffset,y:(overHangWidth)+(roofThickness * 2),z:0.005})
            } else if( params.add_left_front_lean_porch === true && params.add_left_back_lean_porch === true ) {
                setPatternLength((params.lean_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.lean_p_w*2))
                setPatternPosition({x:(params.lean_p_d/2) + overHangWidth + cbFrontBackWallOffset + params.lean_p_w,y:(overHangWidth)+(roofThickness * 2),z:0.005})
            } else {
                setPatternLength(params.lean_p_d + ((overHangLength)*2) + patternLengthOffset )
                setPatternPosition({x:(params.lean_p_d + ((overHangLength)*2) )/2 + patternPositionOffset,y:(overHangWidth)+(roofThickness * 2),z:0.005})
            }

            
            setPatternDepth(diagonalLength)
        }
    }, [params.p_r_s, params.lean_p_d, params.lean_p_w, params.o_v_h_n_g_e, params.o_v_h_n_g, overHangLength, overHangWidth, diagonalLength, cbFrontBackWallOffset, params.add_left_front_lean_porch, params.add_left_back_lean_porch, patternLengthOffset, patternPositionOffset, sideOverHangSize, endOverHangSize, params.leanF_p_w, params.leanB_p_w, regularPatternPositionOffset])

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "leftlean" && params.activeWall === "L_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);

    const texture = useMemo(() => {
        let texture = (const_var.wallTextureObj["vertical"] != undefined) ? const_var.wallTextureObj["vertical"].clone() : const_var.wallTextureObj["vertical"];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])

        return (
        <group {...eventHandlers}>
            {params.p_r_s !== 1 &&
                <group position={[-(params.p_w / 2 + params.lean_p_w + wallThickness * 2), params.lean_p_h + 0.025 - wallThickness + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 + 0.14 * (params.b_l_t_r_p / 12) - 0.015: 0), 0]} rotation={[0, 0, Math.PI / 2 + roofAngle]} >
                    <group rotation={[0, -Math.PI / 2, 0]}>
                        <mesh name='roof-in' position={[0, 0, roofThickness * 2]}>
                            <extrudeGeometry args={[roofPanModel, extrudeSettings(roofThickness)]} />
                            <MaterialInnerRoof texture={roofTexture} visibleSection={'leftLean'} />
                        </mesh>
                    </group>
                </group>
            }
            {/* <group position={[-(params.p_w / 2 + params.lean_p_w + wallThickness * 2), params.lean_p_h + 0.025 - wallThickness, 0]} rotation={[0, 0, Math.PI / 2 + roofAngle]} >
                <group rotation={[0, -Math.PI / 2, 0]}>
                    {isRoofPanel &&
                        <group name='roof-panel-left' rotation={[Math.PI / -2 - roofAngle, 0, 0]} position = {[0,-Math.sqrt(Math.pow(params.lean_p_w, 2) + Math.pow((params.b_l_t_r_p / 12*(params.lean_p_w)), 2)),0]}>
                            <mesh>
                                <extrudeGeometry args={[roofPanel, extrudeSettings(0.03)]} />
                                <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={0.2} toneMapped={false} emissive={'#1B1B1B'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                            </mesh>
                            <mesh position={[0,0,-0.01]}>
                                <extrudeGeometry args={[roofPanel, extrudeSettings(0.03)]} />
                                <meshStandardMaterial  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={0.2} toneMapped={false} emissive={'#1B1B1B'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                            </mesh>
                        </group>
                    }
                </group>
            </group> */}
            <mesh name="roof-out-left"  geometry={roofModelObj?.mainOuter} position={[0, 0.043, 0]} renderOrder={1}>
                <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={'leftLean'} />
            </mesh>
            { (params.p_r_s === 3 && params.add_left_front_lean_porch) &&
                <mesh name='roof-top-right-trim-ridge' position={[0, 0.03, 0]} geometry={roofTopTrimRidgeModelObj?.rightTrimRidge} >
                    <MaterialRoofRidge isPattern={isPattern} visibleSection={'leftLean'} />
                </mesh>
            }
            { (params.p_r_s === 3 && params.add_left_back_lean_porch) &&
                <mesh name='roof-top-left-trim-ridge' position={[0, 0.03, 0]} geometry={roofTopTrimRidgeModelObj?.leftTrimRidge} >
                    <MaterialRoofRidge isPattern={isPattern} visibleSection={'leftLean'} />
                </mesh>
            }
            {params.p_r_s === 1 &&
                <>
                    <mesh name="roof-out-curve" position={[0, 0.03, 0]} geometry={roofModelObj?.curveOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"leftLean"} />
                    </mesh>
                    <mesh name="roof-inner-curve" geometry={roofModelObj?.curveInner}>
                        <MaterialInnerRoof texture={horizontalMap} visibleSection={'leftLean'} />
                    </mesh>
                </>
            }
        </group>
    )
}

export default RoofModel